import React, { useMemo, useState, useEffect, useCallback } from "react";
import { useDispatch, useStore } from "../../../store";
import { setSelectedInteractionType } from "../../../store/drug-interactions/actions";
import { DEFAULT_SELECTED_TYPE } from "../../../store/drug-interactions/reducer";
import DrugInteractionResultsItem from "./ResultItem";
import DrugInteractionResultsHeader from "./Header";
import PropTypes from "prop-types";

import { Type, Alert } from "discover.medical.react-component-library";
import { DrugInteractionsResultsContainer } from "./styled";
import SeverityInfoModal from "../SeverityInfoModal";

const EMPTY_ARRAY = [];

const DrugInteractionsResults = ({ viewModel, handleItemClick }) => {
  const dispatch = useDispatch();
  const [isSeverityModalOpen, setIsSeverityModalOpen] = useState(false);

  const { drugInteractions } = useStore();
  const { inputs, selectedInteractionType } = drugInteractions;

  const {
    severityIndex,
    header: { title: headerTitle },
    content: {
      filterControls,
      results,
      noResultsNotification,
      noDrugToDrugResultsNotification,
    },
  } = viewModel;

  const filterOptions = filterControls.options;
  const severities = severityIndex?.definitions?.content ?? EMPTY_ARRAY;
  const relevantInteractions = results[selectedInteractionType] ?? EMPTY_ARRAY;
  const hasRelevantInteractions = relevantInteractions.length > 0;
  const hasAnyInteractions = useMemo(() => !!Object.keys(results).length, [
    results,
  ]);

  const severityMap = useMemo(
    () =>
      severities.reduce((acc, severity) => {
        acc[severity.severityIndex] = severity;
        return acc;
      }, {}),
    [severities]
  );

  const interactionsLabel = useMemo(() => {
    const names = inputs?.map((item) => item.value).join(", ");
    return `${headerTitle} ${names}`;
  }, [inputs, headerTitle]);

  const handleInfoIconClick = useCallback(() => {
    setIsSeverityModalOpen(true);
  }, []);

  const handleCloseSeverityDialog = useCallback(() => {
    setIsSeverityModalOpen(false);
  }, []);

  const handleChangeSelectedInteractionType = useCallback(
    (selectedType) => setSelectedInteractionType(dispatch, selectedType),
    [dispatch]
  );

  useEffect(() => {
    if (hasRelevantInteractions) {
      return;
    }

    const preselectedType = filterOptions[0]?.type || DEFAULT_SELECTED_TYPE;
    handleChangeSelectedInteractionType(preselectedType);
  }, [
    hasRelevantInteractions,
    filterOptions,
    handleChangeSelectedInteractionType,
  ]);

  const notification = noResultsNotification || noDrugToDrugResultsNotification;

  const headerContent = (
    <>
      <Type
        element="h2"
        fontColor="primary"
        fontFamily="secondary"
        marginBottom="lg"
      >
        {interactionsLabel}
      </Type>
      {notification && (
        <Alert marginBottom="lg" type={notification.type}>
          {notification.content}
        </Alert>
      )}
      {hasAnyInteractions && (
        <>
          <SeverityInfoModal
            isOpen={isSeverityModalOpen}
            handleClose={handleCloseSeverityDialog}
            title={viewModel.severityIndex.title}
            definitions={viewModel.severityIndex.definitions}
            documentationRatings={viewModel.severityIndex.documentationRatings}
          />
          <DrugInteractionResultsHeader
            filterOptions={filterOptions}
            filterLabel={filterControls.label}
            interactionsCount={relevantInteractions.length}
            severityIndex={severityIndex}
            selectedInteractionType={selectedInteractionType}
            setSelectedInteractionType={handleChangeSelectedInteractionType}
            handleInfoIconClick={handleInfoIconClick}
          />
        </>
      )}
    </>
  );

  return (
    <DrugInteractionsResultsContainer id="dic-result-list">
      {headerContent}
      {hasRelevantInteractions && (
        <div data-auto="result-list">
          {relevantInteractions.map((interaction, index) => {
            return (
              <DrugInteractionResultsItem
                key={index}
                interaction={interaction}
                severity={severityMap[interaction.severity]}
                handleItemClick={handleItemClick}
                currentIndex={index}
                totalItems={relevantInteractions.length}
              />
            );
          })}
        </div>
      )}
    </DrugInteractionsResultsContainer>
  );
};

DrugInteractionsResults.displayName = "DrugInteractionsResults";

DrugInteractionsResults.propTypes = {
  handleItemClick: PropTypes.func.isRequired,
  viewModel: PropTypes.shape({
    header: PropTypes.shape({
      title: PropTypes.string.isRequired,
    }),
    content: PropTypes.shape({
      results: PropTypes.object.isRequired,
      filterControls: PropTypes.shape({
        options: PropTypes.array.isRequired,
      }),
    }),
    severityIndex: PropTypes.object,
  }),
};

export default DrugInteractionsResults;
